import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { relativeDate, ticketTitle } from '@/lib/taskRules'
import { EmptyNote, RowLink, RowSkeleton, Section, StatTile, TileGrid } from './shared'
import type { Task } from '@/api/types'
import type { useTaskBuckets } from './useTaskBuckets'

type Buckets = ReturnType<typeof useTaskBuckets>

/** The two dual-reader kinds. A disagreement between two independent reads is
 *  the only thing a radiologist tiebreaks; a mask escalation goes to a
 *  maintainer, so it must not be counted here even though `GET /tasks` returns
 *  it (only technicians are auto-scoped server-side). */
const DUAL_KINDS: Task['task_kind'][] = ['anomaly_annotation', 'pathology_annotation']

/**
 * Radiologist home. A radiologist holds `task:review` but not `study:read_all`,
 * so every number here comes from `GET /tasks` alone — no `['studies','flagged']`
 * query, which would 403 every poll.
 */
export function RadiologistHome({ buckets }: { buckets: Buckets }) {
  const { decidable, available, flagged, isLoading } = buckets
  const anomalyAvailable = available.filter((t) => t.task_kind === 'anomaly_annotation')
  const tiebreaks = flagged.filter((t) => DUAL_KINDS.includes(t.task_kind))

  return (
    <>
      <Section
        title="Reads awaiting my verdict"
        description="Submissions you hold, or that your role can decide. A verdict needs the task — the gateway refuses one you don't hold."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/work?tab=decide">Open needs decision</Link>
          </Button>
        }
      >
        {isLoading ? (
          <RowSkeleton rows={2} />
        ) : decidable.length === 0 ? (
          <EmptyNote>
            Nothing waiting on your verdict. Work arrives here when a technician submits a
            mask you're reviewing — claim the review from{' '}
            <Link to="/work?tab=available" className="underline underline-offset-2">
              Available
            </Link>{' '}
            first.
          </EmptyNote>
        ) : (
          <div className="space-y-2">
            {decidable.slice(0, 5).map((t) => (
              <RowLink
                key={t.id}
                to={`/work?tab=decide&task=${t.id}`}
                // `ticketTitle`, not `t.title`: the stored title is written once at
                // task creation, so a merged radiologist ticket still reads
                // "Review organ mask" long after it became the anomaly read.
                primary={ticketTitle(t)}
                secondary={<span className="font-mono">{t.study_uid}</span>}
                right={relativeDate(t.updated_at)}
              />
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Your reading list"
        description="Each number opens the list it counts — already filtered."
      >
        <TileGrid>
          <StatTile
            to="/work?tab=available"
            label="Anomaly reads available"
            value={anomalyAvailable.length}
            hint="Clinical reads on studies declared anomalous at ingest. Radiologist-only by design."
            emptyHint="No anomaly reads to claim. They appear only for studies ingested as anomalous."
            loading={isLoading}
          />
          <StatTile
            to="/work?tab=decide"
            label="Disagreements I can tiebreak"
            value={tiebreaks.length}
            hint="Two independent reads disagreed, so the workflow escalated instead of auto-finalizing."
            emptyHint="No disagreements. Matching pairs of reads finalize themselves — nothing for you to break."
            tone="attention"
            loading={isLoading}
          />
        </TileGrid>
      </Section>
    </>
  )
}
